import { useId, useRef, useState } from 'react';
import { Icon, Modal, V2Button, type SakhtarIconName } from '@sakhtar/design-system/react';
import './importGuide.css';

const guideSteps: readonly {
  label: string;
  title: string;
  description: string;
  icon: SakhtarIconName;
  items: readonly string[];
  takeaway: string;
}[] = [
  {
    label: 'قالب آماده',
    title: 'از قالب همان بخش شروع کنید',
    description: 'برای هر بخشی که اطلاعات دارید، فایل نمونهٔ همان بخش را دانلود کنید.',
    icon: 'invoice',
    items: [
      'فایل‌های مشتریان، تأمین‌کنندگان، مالی، انبار و فروش مستقل هستند؛ لازم نیست همه را پر کنید.',
      'داده‌ها را در قالب Excel با پسوند .xlsx وارد کنید؛ نام شیت‌ها و عنوان ستون‌ها را تغییر ندهید.',
      'کد مشتری، کالا و انبار را در فایل‌های مرتبط یکسان بنویسید تا ارتباط‌ها قابل تشخیص باشند.',
    ],
    takeaway: 'خروجی این مرحله: فایل‌های آماده با ساختار قابل خواندن.',
  },
  {
    label: 'انتخاب فایل',
    title: 'فایل را در جای درست قرار دهید',
    description:
      'در کارت هر بخش، فایل تکمیل‌شدهٔ همان بخش را انتخاب کنید و سپس بررسی را شروع کنید.',
    icon: 'upload',
    items: [
      'فقط بخش‌هایی را انتخاب کنید که می‌خواهید اطلاعاتشان وارد شود.',
      'حجم هر فایل حداکثر ۵ مگابایت و مجموع فایل‌ها حداکثر ۱۰ مگابایت است.',
      'اگر فایل اشتباه انتخاب شد، پیش از شروع بررسی آن را حذف یا جایگزین کنید.',
    ],
    takeaway: 'خروجی این مرحله: یک فضای بررسی برای فایل‌های انتخاب‌شده.',
  },
  {
    label: 'اصلاح اطلاعات',
    title: 'اول داده‌ها را خوانا و درست کنید',
    description: 'ردیف‌ها، ستون‌ها و خطاهای هر بخش را در جدول بررسی کنید.',
    icon: 'layers',
    items: [
      'برای اصلاح یک رکورد، ویرایش ردیف را باز کنید؛ برای اصلاح چند مقدار در یک ستون، ویرایش ستون را انتخاب کنید.',
      'اصلاحات در پنجرهٔ جداگانه انجام می‌شوند؛ مقدار قبلی و تغییر پیشنهادی را پیش از ذخیره مقایسه کنید.',
      'پس از اصلاح داده‌ها، ارتباط‌ها و پیش‌نمایش را دوباره بررسی کنید؛ ممکن است نتیجه تغییر کند.',
    ],
    takeaway: 'خروجی این مرحله: اطلاعات اصلاح‌شده، آمادهٔ بررسی ارتباط‌ها.',
  },
  {
    label: 'تحلیل هوشمند',
    title: 'دربارهٔ ارتباط‌ها تصمیم بگیرید',
    description:
      'تحلیل هوشمند شباهت رکوردها را بررسی می‌کند؛ تصمیم نهایی برای یکپارچه‌سازی با شماست.',
    icon: 'users',
    items: [
      '«احتمال یکسان بودن» نشان می‌دهد دو رکورد چقدر احتمال دارد یک طرف‌حساب باشند؛ «اطمینان» میزان اطمینان مدل به پیشنهاد خودش است.',
      'دو رکورد را کنار هم ببینید؛ یکسان بودن یا جدا ماندن را مشخص کنید و در صورت ادغام، اطلاعات اصلی را انتخاب کنید.',
      'اگر بررسی ادامه دارد یا متوقف شده، دکمهٔ ادامه یا تلاش دوباره را بزنید. شمارنده‌ها پیشرفت واقعی تحلیل را نشان می‌دهند.',
    ],
    takeaway: 'درصدهای هر پیشنهاد، تضمین صحت کل اطلاعات نیستند.',
  },
  {
    label: 'تأیید و پیش‌نمایش',
    title: 'بخش‌های واردشده را جداگانه تأیید کنید',
    description: 'نتیجهٔ یکپارچه‌سازی و تعداد داده‌های آماده را ببینید؛ بعد پیش‌نمایش بگیرید.',
    icon: 'eye',
    items: [
      'کارت‌های بررسی را باز کنید و داده‌ها، ارتباط‌ها و موارد نیازمند تصمیم را کنترل کنید.',
      'پس از بررسی، تأیید هر بخش واردشده را ثبت کنید؛ وضعیت داده‌های آماده و موارد نیازمند اصلاح را ببینید.',
      'خطاهای پیش‌نمایش باید برطرف شوند. هشدارها را بخوانید و تأیید کنید؛ برای ادامهٔ تحلیل، ابتدا پیش‌نمایش را حذف کنید.',
    ],
    takeaway: 'خروجی این مرحله: پیش‌نمایش قابل تصمیم‌گیری، پیش از ثبت نهایی.',
  },
  {
    label: 'ثبت و نتیجه',
    title: 'ثبت کنید و مقصد هر داده را ببینید',
    description:
      'ثبت نهایی فقط با اقدام شما انجام می‌شود. پس از آن، نتیجه و مقصد رکوردها در دسترس است.',
    icon: 'check',
    items: [
      'تعداد رکوردهای ثبت‌شده و مقصد هر ردیف را در نتیجه بررسی کنید؛ رکوردهای مرتبط ممکن است به یک مقصد مشترک برسند.',
      'سفارش‌ها و اقلام افتتاحیه به‌صورت پیش‌نویس ساخته می‌شوند؛ ثبت ورود اطلاعات به‌تنهایی سند مالی را قطعی نمی‌کند.',
      'برای کنترل نتیجه، مقصد رکورد را باز کنید. با بازگشت به همین فضای بررسی، نتیجهٔ ثبت‌شده قابل بازیابی است.',
    ],
    takeaway: 'خروجی این مرحله: داده‌های ثبت‌شده با مسیر روشن برای پیگیری.',
  },
];

/** Manual walkthrough: opening or navigating it never changes the import workspace. */
export function ImportGuide({
  currentStep = 0,
  maxRows = 1000,
}: {
  currentStep?: number;
  maxRows?: number;
}) {
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);
  const trigger = useRef<HTMLSpanElement>(null);
  const headingId = useId();
  const step = guideSteps[index]!;
  const count = guideSteps.length;
  const format = (value: number) => value.toLocaleString('fa-IR');
  const close = () => {
    setOpen(false);
    trigger.current?.querySelector('button')?.focus();
  };

  return (
    <>
      <span className="import-guide__trigger" ref={trigger}>
        <V2Button
          type="button"
          variant="secondary"
          icon="info"
          aria-haspopup="dialog"
          onClick={() => {
            setIndex(
              Number.isFinite(currentStep)
                ? Math.max(0, Math.min(count - 1, Math.floor(currentStep)))
                : 0,
            );
            setOpen(true);
          }}
        >
          راهنمای مرحله‌به‌مرحله
        </V2Button>
      </span>
      <Modal
        open={open}
        title="راهنمای ورود اطلاعات"
        onClose={close}
        footer={
          <div className="import-guide__footer" dir="rtl">
            <V2Button
              type="button"
              variant="secondary"
              icon="chevronRight"
              disabled={index === 0}
              onClick={() => setIndex(index - 1)}
            >
              مرحلهٔ قبل
            </V2Button>
            {index < count - 1 ? (
              <V2Button type="button" variant="primary" onClick={() => setIndex(index + 1)}>
                مرحلهٔ بعد <Icon name="chevronLeft" size="sm" />
              </V2Button>
            ) : (
              <V2Button type="button" variant="primary" icon="check" onClick={close}>
                متوجه شدم
              </V2Button>
            )}
          </div>
        }
      >
        <section
          className="import-guide"
          dir="rtl"
          aria-roledescription="کاروسل"
          aria-label="آموزش ورود اطلاعات"
          onKeyDown={(event) => {
            if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
            if (event.key === 'ArrowLeft') {
              event.preventDefault();
              setIndex((previous) => Math.min(count - 1, previous + 1));
            } else if (event.key === 'ArrowRight') {
              event.preventDefault();
              setIndex((previous) => Math.max(0, previous - 1));
            } else if (event.key === 'Home' || event.key === 'End') {
              event.preventDefault();
              setIndex(event.key === 'Home' ? 0 : count - 1);
            }
          }}
        >
          <div className="import-guide__progress">
            <span>
              قدم {format(index + 1)} از {format(count)}
            </span>
            <span>{step.label}</span>
          </div>
          <div className="import-guide__track" aria-hidden="true">
            {guideSteps.map((item, position) => (
              <span key={item.label} className={position <= index ? 'is-complete' : undefined} />
            ))}
          </div>
          <div
            className="import-guide__slide"
            key={index}
            role="group"
            aria-roledescription="اسلاید"
            aria-labelledby={headingId}
            aria-live="polite"
            aria-atomic="true"
          >
            <span className="import-guide__illustration" aria-hidden="true">
              <Icon name={step.icon} size="lg" />
            </span>
            <h3 id={headingId}>{step.title}</h3>
            <p className="import-guide__description">{step.description}</p>
            <ol className="import-guide__instructions">
              {step.items.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ol>
            {index === 1 ? (
              <p className="import-guide__limit">
                حداکثر {format(maxRows)} ردیف داده در مجموع فایل‌ها.
              </p>
            ) : null}
            <p className="import-guide__takeaway">
              <Icon name="info" size="sm" />
              {step.takeaway}
            </p>
          </div>
          <nav className="import-guide__navigation" aria-label="انتخاب مرحلهٔ راهنما">
            {guideSteps.map((item, position) => (
              <button
                type="button"
                key={item.label}
                aria-label={`قدم ${format(position + 1)}: ${item.label}`}
                aria-current={position === index ? 'step' : undefined}
                onClick={() => setIndex(position)}
              >
                <span aria-hidden="true" />
              </button>
            ))}
          </nav>
        </section>
      </Modal>
    </>
  );
}
